/**
 * 城池管理页面
 * 以列表形式显示势力所有城池，点击可进入详情
 */

import { useState, useMemo } from 'react'
import { useShangGameStore } from '../stores/shangGameStore'
import { SHANG_CITIES, SHANG_FACTIONS } from '../data/shangDynastyData'
import type { CityStatus } from '../types/city'
import './CityManagementPage.css'

interface CityManagementPageProps {
  factionId: string
  onBack?: () => void
  embedded?: boolean // 是否嵌入在内政管理页面中
}

type SortKey = 'name' | 'population' | 'type'
type SortOrder = 'asc' | 'desc'
type ViewMode = 'list' | 'grid'

/** 城邑等级 */
type CityLevel = 'capital' | 'large' | 'medium' | 'small'

/** 资源结构 */
interface CityResources {
  food: number
  money: number
  wood: number
  metal: number
}

// 简化的城邑数据接口（基于 shangGameStore 的 CityState）
interface SimpleCity {
  id: string
  name: string
  type: CityLevel
  status: CityStatus
  population: number
  terrain: string
  factionId: string
  isCapital: boolean
  // 新增信息维度
  description: string
  buildings: string[]
  resources: CityResources
  // 游戏内动态数据
  gamePopulation: number
  morale: number
  garrison: number
}

export function CityManagementPage({ factionId, onBack, embedded = false }: CityManagementPageProps) {
  const [selectedCity, setSelectedCity] = useState<SimpleCity | null>(null)
  const [filterType, setFilterType] = useState<CityLevel | 'all'>('all')
  const [filterStatus, setFilterStatus] = useState<CityStatus | 'all'>('all')
  const [sortKey, setSortKey] = useState<SortKey>('name')
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc')
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<ViewMode>('list')

  // 获取游戏状态
  const { cityStates, factionStates, getPlayerCapital } = useShangGameStore()

  // 获取势力信息
  const faction = SHANG_FACTIONS.find(f => f.id === factionId)
  const factionState = factionStates[factionId]

  // 构建城邑列表数据
  const cities: SimpleCity[] = useMemo(() => {
    if (!factionState) return []

    const playerCapital = getPlayerCapital()

    return factionState.ownedCities.map(cityId => {
      // 从静态数据获取城邑基本信息
      const staticCity = SHANG_CITIES.find(c => c.id === cityId)
      // 从游戏状态获取动态数据
      const gameCity = cityStates[cityId]

      if (!staticCity) return null

      return {
        id: cityId,
        name: staticCity.name,
        type: staticCity.level,
        status: (gameCity?.morale || 50) > 70 ? 'prosperous' : (gameCity?.morale || 50) > 40 ? 'stable' : 'unrest',
        population: staticCity.population,
        terrain: staticCity.terrain as string,
        factionId: staticCity.factionId,
        isCapital: playerCapital?.cityId === cityId,
        // 新增信息维度
        description: staticCity.description,
        buildings: staticCity.buildings,
        resources: staticCity.resources,
        // 游戏内动态数据
        gamePopulation: gameCity?.population || staticCity.population,
        morale: gameCity?.morale || 50,
        garrison: gameCity?.garrison || 0,
      } as SimpleCity
    }).filter((c): c is SimpleCity => c !== null)
  }, [factionState, cityStates, getPlayerCapital])

  // 过滤和排序城邑
  const filteredCities = useMemo(() => {
    let result = [...cities]

    // 搜索过滤
    if (searchQuery) {
      result = result.filter(city =>
        city.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        city.terrain.includes(searchQuery)
      )
    }

    // 类型过滤
    if (filterType !== 'all') {
      result = result.filter(city => city.type === filterType)
    }

    // 状态过滤
    if (filterStatus !== 'all') {
      result = result.filter(city => city.status === filterStatus)
    }

    // 排序
    result.sort((a, b) => {
      let comparison = 0
      switch (sortKey) {
        case 'name':
          comparison = a.name.localeCompare(b.name)
          break
        case 'population':
          comparison = a.gamePopulation - b.gamePopulation
          break
        case 'type':
          comparison = getTypePriority(a.type) - getTypePriority(b.type)
          break
      }
      return sortOrder === 'asc' ? comparison : -comparison
    })

    return result
  }, [cities, searchQuery, filterType, filterStatus, sortKey, sortOrder])

  // 统计数据
  const stats = useMemo(() => {
    return {
      total: cities.length,
      capitals: cities.filter(c => c.isCapital).length,
      prosperous: cities.filter(c => c.status === 'prosperous').length,
      unrest: cities.filter(c => c.status === 'unrest').length,
      totalPopulation: cities.reduce((sum, c) => sum + c.gamePopulation, 0),
    }
  }, [cities])

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortOrder('desc')
    }
  }

  if (!faction) {
    return (
      <div className="city-management-page">
        <div className="empty-state">
          <span className="empty-icon">🏛</span>
          <p>未找到势力信息</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`city-management-page ${embedded ? 'embedded' : ''}`}>
      {!embedded && (
        <header className="page-header">
          <div className="header-left">
            {onBack && (
              <button className="back-btn" onClick={onBack}>
                ← 返回
              </button>
            )}
            <div className="faction-info">
              <div
                className="faction-color"
                style={{ backgroundColor: faction.color }}
              />
              <div className="faction-details">
                <h1>{faction.name}</h1>
                <span className="subtitle">城池管理</span>
              </div>
            </div>
          </div>

          <div className="header-stats">
            <StatBadge icon="🏛" value={stats.total} label="城池总数" />
            <StatBadge icon="👑" value={stats.capitals} label="国都" />
            <StatBadge icon="👥" value={stats.totalPopulation.toLocaleString()} label="总人口" />
            <StatBadge icon="😊" value={stats.prosperous} label="繁荣" color="#4caf50" />
            <StatBadge icon="😠" value={stats.unrest} label="动荡" color="#f44336" />
          </div>
        </header>
      )}

      {/* 工具栏 */}
      <div className="toolbar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="搜索城邑名称或地形..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="filters">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as CityLevel | 'all')}
            className="filter-select"
          >
            <option value="all">所有类型</option>
            <option value="capital">国都</option>
            <option value="large">大城</option>
            <option value="medium">中城</option>
            <option value="small">小城</option>
          </select>

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as CityStatus | 'all')}
            className="filter-select"
          >
            <option value="all">所有状态</option>
            <option value="prosperous">繁荣</option>
            <option value="stable">安定</option>
            <option value="recovering">恢复中</option>
            <option value="unrest">动荡</option>
          </select>
        </div>

        <div className="view-toggle">
          <button
            className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
            title="列表视图"
          >
            ☰
          </button>
          <button
            className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
            onClick={() => setViewMode('grid')}
            title="网格视图"
          >
            ⊞
          </button>
        </div>
      </div>

      {/* 排序栏 */}
      <div className="sort-bar">
        <span className="sort-label">排序:</span>
        <SortButton
          label="名称"
          active={sortKey === 'name'}
          order={sortOrder}
          onClick={() => handleSort('name')}
        />
        <SortButton
          label="类型"
          active={sortKey === 'type'}
          order={sortOrder}
          onClick={() => handleSort('type')}
        />
        <SortButton
          label="人口"
          active={sortKey === 'population'}
          order={sortOrder}
          onClick={() => handleSort('population')}
        />
      </div>

      {/* 城邑列表 */}
      <div className={`city-container ${viewMode}`}>
        {filteredCities.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">🏛</span>
            <p>没有找到符合条件的城邑</p>
          </div>
        ) : (
          filteredCities.map((city) => (
            viewMode === 'list' ? (
              <CityListItem
                key={city.id}
                city={city}
                onClick={() => setSelectedCity(city)}
              />
            ) : (
              <CityGridCard
                key={city.id}
                city={city}
                onClick={() => setSelectedCity(city)}
              />
            )
          ))
        )}
      </div>

      {/* 城邑详情弹窗 */}
      {selectedCity && (
        <CityDetailModal
          city={selectedCity}
          onClose={() => setSelectedCity(null)}
        />
      )}
    </div>
  )
}

// ============================================
// 子组件
// ============================================

function StatBadge({ icon, value, label, color }: { icon: string; value: number | string; label: string; color?: string }) {
  return (
    <div className="stat-badge">
      <span className="badge-icon">{icon}</span>
      <div className="badge-content">
        <span className="badge-value" style={{ color }}>{value}</span>
        <span className="badge-label">{label}</span>
      </div>
    </div>
  )
}

function SortButton({ label, active, order, onClick }: { label: string; active: boolean; order: SortOrder; onClick: () => void }) {
  return (
    <button className={`sort-btn ${active ? 'active' : ''}`} onClick={onClick}>
      {label}
      {active && <span className="sort-arrow">{order === 'asc' ? '↑' : '↓'}</span>}
    </button>
  )
}

function CityListItem({ city, onClick }: { city: SimpleCity; onClick: () => void }) {
  return (
    <div className="city-list-item" onClick={onClick}>
      <div className="city-main-info">
        <div className="city-name-section">
          {city.isCapital && <span className="capital-star">★</span>}
          <span className="city-name">{city.name}</span>
          <span className={`type-tag ${city.type}`}>{getTypeLabel(city.type)}</span>
        </div>
        <div className="city-region">{getTerrainLabel(city.terrain)}</div>
      </div>

      <div className="city-stats-row">
        <div className="stat-item">
          <span className="stat-icon">👥</span>
          <span className="stat-value">{city.gamePopulation.toLocaleString()}</span>
        </div>
        <div className="stat-item">
          <span className="stat-icon">💪</span>
          <span className="stat-value">{city.morale}%</span>
        </div>
        <div className="stat-item">
          <span className="stat-icon">⚔</span>
          <span className="stat-value">{city.garrison}</span>
        </div>
      </div>

      <div className="city-status-section">
        <div className="morale-bar">
          <div className="morale-fill" style={{ width: `${city.morale}%` }} />
        </div>
        <span className={`status-badge ${city.status}`}>{getStatusLabel(city.status)}</span>
      </div>

      <div className="city-arrow">→</div>
    </div>
  )
}

function CityGridCard({ city, onClick }: { city: SimpleCity; onClick: () => void }) {
  return (
    <div className="city-grid-card" onClick={onClick}>
      <div className="card-header">
        <div className="card-title">
          {city.isCapital && <span className="capital-star">★</span>}
          <span className="city-name">{city.name}</span>
        </div>
        <span className={`status-dot ${city.status}`} />
      </div>

      <div className="card-type">{getTypeLabel(city.type)} · {getTerrainLabel(city.terrain)}</div>

      <div className="card-stats">
        <div className="card-stat">
          <span className="stat-label">人口</span>
          <span className="stat-value">{city.gamePopulation.toLocaleString()}</span>
        </div>
        <div className="card-stat">
          <span className="stat-label">民心</span>
          <span className="stat-value">{city.morale}%</span>
        </div>
        <div className="card-stat">
          <span className="stat-label">驻军</span>
          <span className="stat-value">{city.garrison}</span>
        </div>
      </div>

      <div className="card-footer">
        <div className="morale-indicator">
          <div className="morale-bar-mini">
            <div className="morale-fill" style={{ width: `${city.morale}%`, backgroundColor: getMoraleColor(city.morale) }} />
          </div>
        </div>
        <span className="view-hint">点击查看</span>
      </div>
    </div>
  )
}

// 简化的详情弹窗
function CityDetailModal({ city, onClose }: { city: SimpleCity; onClose: () => void }) {
  return (
    <div className="city-detail-overlay" onClick={onClose}>
      <div className="city-detail-modal" onClick={(e) => e.stopPropagation()}>
        <header className="detail-header">
          <h2>
            {city.isCapital && <span className="capital-star">★</span>}
            {city.name}
          </h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </header>

        <div className="detail-content">
          {/* 历史背景 */}
          <div className="detail-section">
            <h3>🏛 历史背景</h3>
            <p className="detail-description">{city.description}</p>
          </div>

          {/* 基本信息 */}
          <div className="detail-section">
            <h3>📋 基本信息</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">类型</span>
                <span className="detail-value">{getTypeLabel(city.type)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">地形</span>
                <span className="detail-value">{getTerrainLabel(city.terrain)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">状态</span>
                <span className={`detail-value status-${city.status}`}>{getStatusLabel(city.status)}</span>
              </div>
            </div>
          </div>

          {/* 人口与民心 */}
          <div className="detail-section">
            <h3>👥 人口与民心</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">当前人口</span>
                <span className="detail-value">{city.gamePopulation.toLocaleString()}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">初始人口</span>
                <span className="detail-value">{city.population.toLocaleString()}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">民心</span>
                <span className="detail-value">{city.morale}%</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">驻军</span>
                <span className="detail-value">{city.garrison}</span>
              </div>
            </div>
          </div>

          {/* 资源产出 */}
          <div className="detail-section">
            <h3>💰 资源储备</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">🌾 粮食</span>
                <span className="detail-value">{city.resources.food.toLocaleString()}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">💵 金钱</span>
                <span className="detail-value">{city.resources.money.toLocaleString()}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">🪵 木材</span>
                <span className="detail-value">{city.resources.wood.toLocaleString()}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">⚙️ 金属</span>
                <span className="detail-value">{city.resources.metal.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* 特殊建筑 */}
          <div className="detail-section">
            <h3>🏗 特殊建筑</h3>
            <div className="buildings-list">
              {city.buildings.map((building, index) => (
                <span key={index} className="building-tag">{building}</span>
              ))}
            </div>
          </div>
        </div>

        <footer className="detail-footer">
          {!city.isCapital && (
            <button className="action-btn primary">
              设为国都
            </button>
          )}
          <button className="action-btn" onClick={onClose}>
            关闭
          </button>
        </footer>
      </div>
    </div>
  )
}

// ============================================
// 辅助函数
// ============================================

function getTypePriority(type: CityLevel): number {
  const priorities: Record<CityLevel, number> = {
    capital: 0,
    large: 1,
    medium: 2,
    small: 3,
  }
  return priorities[type] ?? 99
}

function getTypeLabel(type: CityLevel): string {
  const labels: Record<CityLevel, string> = {
    capital: '国都',
    large: '大城',
    medium: '中城',
    small: '小城',
  }
  return labels[type] ?? type
}

function getStatusLabel(status: CityStatus): string {
  const labels: Record<CityStatus, string> = {
    prosperous: '繁荣',
    stable: '安定',
    recovering: '恢复中',
    unrest: '动荡',
    occupied: '被占领',
    besieged: '被围困',
    ruined: '荒废',
  }
  return labels[status] ?? status
}

function getTerrainLabel(terrain: string): string {
  const labels: Record<string, string> = {
    plain: '平原',
    mountain: '山地',
    river: '河流',
    desert: '沙漠',
    grassland: '草原',
    forest: '森林',
    marsh: '沼泽',
    coast: '海岸',
  }
  return labels[terrain] ?? terrain
}

function getMoraleColor(morale: number): string {
  if (morale >= 70) return '#4caf50'
  if (morale >= 40) return '#ff9800'
  return '#f44336'
}
